// Simple optimized hooks
import { useState, useEffect, useRef } from 'react';

// Media query hook for responsive design
export const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);
  
  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);

    const handler = (event) => setMatches(event.matches);
    mediaQuery.addListener(handler);
    
    return () => mediaQuery.removeListener(handler);
  }, [query]);

  return matches;
};

// Simple scroll tracking hook
export const useScrollTracking = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const scrollTimeout = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      // Update scroll state immediately for smooth UI
      setIsScrolled(window.scrollY > 50);
      
      // Debounce section detection for better performance
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }

      scrollTimeout.current = setTimeout(() => {
        const sections = ['hero', 'about', 'skills', 'projects', 'contact'];
        const viewportHeight = window.innerHeight;
        const scrollPosition = window.scrollY;
        const buffer = viewportHeight * 0.2; // 20% of viewport height for better detection

        // Find the most visible section
        let maxVisibility = 0;
        let mostVisibleSection = null;

        sections.forEach(sectionId => {
          const element = document.getElementById(sectionId);
          if (!element) return;

          const rect = element.getBoundingClientRect();
          const elementTop = rect.top + scrollPosition;
          const elementBottom = elementTop + rect.height;
          
          // Calculate how much of the section is visible
          const visibleTop = Math.max(scrollPosition, elementTop);
          const visibleBottom = Math.min(scrollPosition + viewportHeight, elementBottom);
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);
          
          if (visibleHeight > maxVisibility) {
            maxVisibility = visibleHeight;
            mostVisibleSection = sectionId;
          }
        });

        if (mostVisibleSection && mostVisibleSection !== activeSection) {
          setActiveSection(mostVisibleSection);
        }
      }, 100); // Small delay for performance
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, [activeSection]);

  return { isScrolled, activeSection, setActiveSection };
};

// Simple typewriter effect with improved timing and reliability
export const useTypewriter = (texts, speed = 100) => {
  const [displayText, setDisplayText] = useState('');
  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [reverse, setReverse] = useState(false);
  const [pause, setPause] = useState(true);
  
  useEffect(() => {
    if (!texts?.length) return;
    
    // Initial pause before starting
    if (pause && subIndex === 0 && !reverse) {
      const initialPause = setTimeout(() => {
        setPause(false);
      }, 500);
      return () => clearTimeout(initialPause);
    }
    
    // Pause at the end of a word
    if (pause && subIndex === texts[index].length) {
      const pauseTimeout = setTimeout(() => {
        setPause(false);
        setReverse(true);
      }, 2000); // Longer pause at the end of the word
      return () => clearTimeout(pauseTimeout);
    }

    // Pause before starting new word
    if (pause && subIndex === 0 && reverse) {
      const newWordPause = setTimeout(() => {
        setPause(false);
        setReverse(false);
        setIndex((prev) => (prev + 1) % texts.length);
      }, 500);
      return () => clearTimeout(newWordPause);
    }

    const timeout = setTimeout(() => {
      if (!pause) {
        if (!reverse) {
          if (subIndex < texts[index].length) {
            setDisplayText(texts[index].substring(0, subIndex + 1));
            setSubIndex(prev => prev + 1);
            if (subIndex + 1 === texts[index].length) {
              setPause(true);
            }
          }
        } else {
          if (subIndex > 0) {
            setDisplayText(texts[index].substring(0, subIndex - 1));
            setSubIndex(prev => prev - 1);
          } else {
            setPause(true);
          }
        }
      }
    }, reverse ? speed / 2 : speed);
    
    return () => clearTimeout(timeout);
  }, [texts, index, subIndex, reverse, pause, speed]);

  return displayText;
};

// Simple lazy loading hook
export const useLazyLoad = (threshold = 0.01, rootMargin = '200px 0px') => {
  const [loaded, setLoaded] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLoaded(true);
          if (ref.current) {
            observer.unobserve(ref.current);
          }
        }
      },
      { threshold, rootMargin }
    );

    const currentElement = ref.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [threshold, rootMargin]);

  return { ref, loaded };
};

// Simple intersection observer hook
export const useIntersectionObserver = (options = {}) => {
  const [entries, setEntries] = useState({});
  const elementsRef = useRef(new Map());
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (observedEntries) => {
        const updatedEntries = { ...entries };
        observedEntries.forEach(entry => {
          updatedEntries[entry.target.dataset.observerId] = entry;
        });
        setEntries(updatedEntries);
      },
      {
        threshold: options.threshold || 0.1,
        rootMargin: options.rootMargin || '0px',
        root: options.root || null
      }
    );

    // Observe all existing elements
    elementsRef.current.forEach((element) => {
      if (observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [options.threshold, options.rootMargin, options.root]);

  // Function to register elements to be observed
  const observe = (element, id) => {
    if (!element || !id) return;

    element.dataset.observerId = id;
    elementsRef.current.set(id, element);

    if (observerRef.current) {
      observerRef.current.observe(element);
    }
  };

  // Function to stop observing elements
  const unobserve = (id) => {
    const element = elementsRef.current.get(id);
    if (element && observerRef.current) {
      observerRef.current.unobserve(element);
      elementsRef.current.delete(id);
    }
  };

  return { observe, unobserve, entries };
};
